<template>
  <v-card class="pt-6 pb-3 px-8 ma-0" :class="{ 'rounded-t-0': editMode }">
    <TemplateLabel v-if="isTemplate" transition="fade-transition" />

    <v-card-title class="pa-0 mb-2 d-flex justify-space-between align-top">
      <p class="ma-0 pa-0 h6 blue-grey--text text--darken-3">
        {{ $t('checklist.checklist_one') }}
      </p>
      <p class="ma-0 pa-0 caption blue-grey--text">
        {{ $t('common.required_fields') }}*
      </p>
    </v-card-title>
    <v-card-subtitle class="pa-0 ma-0 blue-grey--text text--darken-1 body-1">
      {{ sectionSubtitle }}
    </v-card-subtitle>

    <v-divider class="my-6" />

    <v-form ref="checklistForm">
      <Draggable
        data-testid="routineFormChecklist-draggable-list"
        ghost-class="ghost-item"
        handle=".item-drag-control"
        :value="visibleItemEntries"
        @change="handleDrag"
      >
        <Item
          v-for="entry in visibleItemEntries"
          class="item-container mb-4"
          :active="entry.originalIndex === activeIndex"
          :data-testid="`routineFormChecklist-item-question-${entry.item.id || entry.originalIndex}`"
          :dependentCount="dependentCountsByIndex[entry.originalIndex]"
          :disabled="isSaving"
          :eligibleTriggers="eligibleTriggersByIndex[entry.originalIndex]"
          :hideNotificationAndFindingButtons="hideNotificationAndFindingButtons"
          :index="entry.originalIndex"
          :isConditionalLogicEnabled="conditionalLogicEnabled"
          :isInsideSeparator="itemSeparatorStates[entry.originalIndex]"
          :isSaving="isSaving"
          :item="entry.item"
          :key="getItemKey(entry)"
          :onlyShowIfInViewport="itemsRenderingShouldBeOptimized"
          :sectionBorderColor="itemSectionColors[entry.originalIndex]"
          @add="addItem($event, entry.originalIndex)"
          @change="updateItem($event, entry.originalIndex)"
          @click="activeIndex = entry.originalIndex"
          @copy="copyItem($event, entry.originalIndex)"
          @copySection="copySectionWithItems(entry.originalIndex)"
          @remove="removeItem(entry.originalIndex)"
          @requestConditionalRemovalConfirmation="
            handleRequestConditionalRemovalConfirmation(
              $event,
              entry.originalIndex
            )
          "
        />
      </Draggable>
    </v-form>

    <ChecklistMenu @addItem="addItem" :disabled="isSaving" />

    <v-card-actions class="pa-0 mb-0 pt-4 buttons-container pb-3 px-0">
      <div class="d-flex justify-end w-100" v-if="editMode">
        <Button
          plain
          class="me-4"
          color="grey"
          data-testid="routineFormChecklist-button-cancel"
          :disabled="isSaving"
          :text="$t('common.button.cancel')"
          @click="$emit('cancel')"
        />
        <Button
          color="primary"
          class="white--text"
          data-testid="routineFormChecklist-button-save"
          :disabled="isSaving"
          :loading="isSaving"
          :text="$t('common.button.save')"
          @click="handleSave"
        />
      </div>

      <div v-else class="d-flex justify-end w-100">
        <Button
          class="me-2"
          color="grey"
          data-testid="routineFormChecklist-button-back"
          icon="mdi-chevron-left"
          plain
          :disabled="isSaving"
          :text="$t('common.button.back')"
          @click="$emit('back')"
        />

        <Button
          v-if="!hideSkipOption"
          class="me-2"
          plain
          color="grey"
          data-testid="routineFormChecklist-button-skip"
          :disabled="isSaving"
          :text="$t('common.button.skip')"
          @click="$emit('skip')"
        />
        <Button
          plain
          iconPosition="right"
          color="primary"
          icon="mdi-chevron-right"
          class="white--text"
          data-testid="routineFormChecklist-button-next"
          :disabled="isSaving"
          :loading="isSaving"
          :text="$t('common.button.next')"
          @click="handleNext"
        />
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
import _ from 'lodash'
import Draggable from 'vuedraggable'
import { utils } from 'frontend-js-shared'

import Button from '@/components/UI/Button'
import TemplateLabel from '@/components/TemplateLabel.vue'
import { SCORING_MODES } from '@/constants'
import {
  getSectionQuestions,
  hasOrphanQuestions
} from '@/containers/RoutineDefinitionFormView/scoring/sectionCompatibility'
import { resolveScoringItemId } from '@/containers/RoutineDefinitionFormView/scoring/scoringShared'
import getVisibilityRuleErrors from '@/containers/RoutineDefinitionFormView/conditionalLogic/getVisibilityRuleErrors'
import getEligibleTriggerQuestions from '@/containers/RoutineDefinitionFormView/conditionalLogic/getEligibleTriggerQuestions'
import {
  removeInvalidVisibilityRules,
  getDependentItems,
  buildErrorDialogPayload
} from '@/containers/RoutineDefinitionFormView/conditionalLogic/conditionalRemoval'
import { getChecklistSectionColor } from '@/lib/getChecklistSectionColor'
import mixin from '@/mixins/mixin.vue'

import { ITEM_TYPES } from './constants.js'
import ChecklistMenu from './ChecklistMenu.vue'
import Item from './Item.vue'
import { createItemId, ensureItemIds } from './itemIds'
import { createOption, ensureOptionIds } from './optionIds'
import { moveVisibleItemGroups } from './visibleItemGroups'

export default {
  name: 'Checklist',

  mixins: [mixin],

  components: {
    Draggable,
    Item,
    ChecklistMenu,
    Button,
    TemplateLabel,
  },

  props: {
    checklist: {
      required: false,
      type: Object,
    },
    editMode: {
      required: false,
      type: Boolean,
    },
    isSaving: {
      required: false,
      type: Boolean,
    },
    isTemplate: {
      type: Boolean,
    },
    subtitle: {
      type: String,
      default: null,
    },
    hideSkipOption: {
      type: Boolean,
      default: false,
    },
    hideNotificationAndFindingButtons: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      items: this.buildInitialItems(),
      activeIndex: -1,
      // Set once the feature flags resolve; data()-initialization and the
      // checklist watcher call the method directly — they can run earlier.
      conditionalLogicEnabled: false,
    }
  },

  async created() {
    await this.$onFeatureFlagsReady()

    this.conditionalLogicEnabled = this.isConditionalLogicInRoutinesEnabled()

    if (this.conditionalLogicEnabled) {
      this.items = ensureItemIds(this.items)
    }
  },

  computed: {
    sectionSubtitle() {
      if (!_.isNil(this.subtitle)) {
        return this.subtitle
      }

      const baseSubtitle = this.$t('checklist.subtitle_routine')

      if (!this.conditionalLogicEnabled) {
        return baseSubtitle
      }

      return `${baseSubtitle} ${this.$t('checklist.visibility_rule.subtitle_suffix')}`
    },

    isSectionScoringActive() {
      return this.checklist?.scoringType === SCORING_MODES.SECTION
    },

    itemsRenderingShouldBeOptimized() {
      return (this.items?.length ?? 0) > 100
    },

    // Compute which items are inside separators once, for performance
    itemSeparatorStates() {
      const states = new Array(this.items.length).fill(false)
      let lastSeparatorIndex = -1

      this.items.forEach((item, index) => {
        if (item.type === ITEM_TYPES.SEPARATOR) {
          lastSeparatorIndex = index
          return
        }

        if (lastSeparatorIndex >= 0) {
          states[index] = true
        }
      })

      return states
    },

    itemSectionColors() {
      const sectionColors = new Array(this.items.length).fill(null)
      let sectionIndex = -1

      this.items.forEach((item, index) => {
        if (item.type === ITEM_TYPES.SEPARATOR) {
          sectionIndex += 1
        }

        sectionColors[index] = getChecklistSectionColor(sectionIndex)
      })

      return sectionColors
    },

    visibleItemEntries() {
      return this.items.reduce((result, item, index) => {
        result.push({
          item,
          originalIndex: index,
        })

        return result
      }, [])
    },

    eligibleTriggersByIndex() {
      if (!this.conditionalLogicEnabled) {
        return {}
      }

      return this.items.reduce((result, item, index) => {
        result[index] = getEligibleTriggerQuestions({
          items: this.items,
          conditionedItemIndex: index,
        })
        return result
      }, {})
    },

    dependentCountsByIndex() {
      if (!this.conditionalLogicEnabled) {
        return {}
      }

      return this.items.reduce((result, item, index) => {
        result[index] = getDependentItems({
          items: this.items,
          sourceItems: [item],
        }).length
        return result
      }, {})
    },
  },

  watch: {
    checklist(newValue) {
      if (!newValue.items) return

      if (!this.isConditionalLogicInRoutinesEnabled()) {
        this.items = newValue.items
        return
      }

      this.items = ensureItemIds(newValue.items)
    },
  },

  methods: {
    isConditionalLogicInRoutinesEnabled() {
      return this.$isFeatureEnabled(
        this.$featuresKeys?.conditionalLogicInRoutines
      )
    },

    buildDefaultItem() {
      const defaultItem = {
        type: ITEM_TYPES.TEXT,
        name: '',
      }

      if (this.isConditionalLogicInRoutinesEnabled()) {
        defaultItem._id = createItemId()
      }

      return defaultItem
    },

    buildInitialItems() {
      if (!this.checklist?.items) {
        return [this.buildDefaultItem()]
      }

      if (!this.isConditionalLogicInRoutinesEnabled()) {
        return this.checklist.items
      }

      return ensureItemIds(this.checklist.items)
    },

    buildCopiedItem(item) {
      const clonedItem = globalThis.structuredClone(item)

      if (!this.conditionalLogicEnabled) {
        return _.omit(clonedItem, ['id', '_id'])
      }

      // The copy drops the editor-only `id` and keeps a fresh persisted
      // `_id` (rules and backend duplication reference it). It also drops
      // `visibilityRules`: copies never reference another question's rules.
      return {
        ..._.omit(clonedItem, ['id', 'visibilityRules']),
        _id: createItemId(),
      }
    },

    itemTypeHasOptions(type) {
      return (
        type === ITEM_TYPES.RADIOS ||
        type === ITEM_TYPES.CHECKBOXES ||
        type === ITEM_TYPES.SELECT
      )
    },

    normalizeItemByType(item, type) {
      const baseItem = {
        ..._.omit(item, ['options', 'min', 'max', 'includeTime']),
        type,
      }

      if (this.itemTypeHasOptions(type)) {
        return {
          ...baseItem,
          options: item.options?.length
            ? ensureOptionIds(item.options)
            : [createOption()],
        }
      }

      if (type === ITEM_TYPES.SCALE) {
        return {
          ...baseItem,
          min: Number.isFinite(item.min) ? item.min : 0,
          max: Number.isFinite(item.max) ? item.max : null,
        }
      }

      if (type === ITEM_TYPES.DATE) {
        return {
          ...baseItem,
          includeTime: Boolean(item.includeTime),
        }
      }

      return baseItem
    },

    validate() {
      return this.$refs.checklistForm.validate()
    },

    copyItem(itemToCopy, index) {
      const newItem = this.buildCopiedItem(itemToCopy)

      if (this.itemTypeHasOptions(newItem?.type)) {
        newItem.options = ensureOptionIds(newItem.options, { forceNew: true })
      }

      this.items.splice(index + 1, 0, newItem)
      this.items = [...this.items]

      this.handleChange()
    },

    copySectionWithItems(separatorIndex) {
      let endIndex = this.items.length
      for (let i = separatorIndex + 1; i < this.items.length; i++) {
        if (this.items[i].type === 'separator') {
          endIndex = i
          break
        }
      }

      const sectionItems = this.items.slice(separatorIndex, endIndex)

      // Fresh client-generated ids; buildCopiedItem drops rules, so the
      // copy starts fresh.
      const clonedItems = sectionItems.map(item => {
        const clonedItem = this.buildCopiedItem(item)

        if (!this.itemTypeHasOptions(clonedItem?.type)) {
          return clonedItem
        }

        return {
          ...clonedItem,
          options: ensureOptionIds(clonedItem.options, { forceNew: true }),
        }
      })

      this.items.splice(endIndex, 0, ...clonedItems)
      this.items = [...this.items]

      this.handleChange()
    },

    async removeItem(index) {
      const removedItem = this.items[index]
      const dependentItems = this.conditionalLogicEnabled
        ? getDependentItems({
            items: this.items,
            sourceItems: [removedItem],
          }).filter(entry => entry.index !== index)
        : []

      if (!_.isEmpty(dependentItems)) {
        const itemsWithoutDependentRules = removeInvalidVisibilityRules({
          items: this.items,
          itemsWithError: dependentItems,
        })
        itemsWithoutDependentRules.splice(index, 1)

        this.$emit('requestConditionalRemoval', {
          items: itemsWithoutDependentRules,
          affectedCount: dependentItems.length,
        })
        return
      }

      const newItems = [...this.items]
      newItems.splice(index, 1)

      const blockedAction = this.getBlockedSectionDeleteAction(index)
      if (blockedAction?.type === 'warning') {
        this.$emit('requestWarning', blockedAction)
        return
      }

      if (blockedAction?.type === 'deleteSectionDecision') {
        this.$emit('requestDeleteSectionDecision', blockedAction)
        return
      }

      this.items = newItems

      await this.$nextTick()

      this.handleChange()
    },

    handleRequestConditionalRemovalConfirmation({ change }, index) {
      if (!this.conditionalLogicEnabled) return

      const currentItem = this.items[index]
      const dependentItems = getDependentItems({
        items: this.items,
        sourceItems: [currentItem],
      })

      if (_.isEmpty(dependentItems)) return

      const isTypeChange =
        Boolean(change?.type) && change.type !== currentItem.type
      const updatedItem = isTypeChange
        ? this.normalizeItemByType({ ...currentItem, ...change }, change.type)
        : { ...currentItem, ...change }

      const items = removeInvalidVisibilityRules({
        items: this.items,
        itemsWithError: dependentItems,
      })
      items[index] = updatedItem

      this.$emit('requestConditionalRemoval', {
        items,
        affectedCount: dependentItems.length,
      })
    },

    addItem(type = 'text', index = this.items.length) {
      const newItem = {
        name: '',
        type,
        referenceMedia: [],
        min: 0,
      }

      if (this.conditionalLogicEnabled) {
        newItem._id = createItemId()
      }

      // Only a separator re-homes items into a new section and can break rules.
      const isGatedSectionInsertion =
        type === ITEM_TYPES.SEPARATOR && this.conditionalLogicEnabled
      const previousItems = isGatedSectionInsertion
        ? globalThis.structuredClone(this.items)
        : null

      this.items.splice(index + 1, 0, newItem)

      if (!isGatedSectionInsertion) {
        this.handleChange()
        return
      }

      const itemsWithError = getVisibilityRuleErrors(this.items)

      if (_.isEmpty(itemsWithError)) {
        this.handleChange()
        return
      }

      // Landed action: the section stays while the dialog decides; cancel
      // reverts the insertion through previousItems.
      this.emitConditionalRemovalForErrors(itemsWithError, previousItems)
    },

    updateItem(value, index) {
      const currentItem = this.items[index]

      if (Boolean(value?.type) && value.type !== currentItem.type) {
        const nextItem = this.normalizeItemByType(
          { ...currentItem, ...value },
          value.type
        )
        this.items = [...this.items]
        this.items[index] = nextItem
        this.handleChange()
        return
      }

      this.items = [...this.items]
      this.items[index] = { ...currentItem, ...value }

      this.handleChange()
    },

    getItemKey(entry) {
      if (this.conditionalLogicEnabled && entry.item._id) {
        return entry.item._id
      }

      return entry.item.id || `visible-item-${entry.originalIndex}`
    },

    handleChange() {
      this.$emit('checklistIsValid', this.validate())
      this.$emit('change', this.items)
    },

    handleSave() {
      const valid = this.validate()
      if (!valid) return
      this.$emit('save', this.items)
    },

    handleNext() {
      if (this.conditionalLogicEnabled && !this.validate()) return

      this.$emit('next', this.items)
    },

    emitConditionalRemovalForErrors(itemsWithError, previousItems) {
      this.$emit(
        'requestConditionalRemoval',
        buildErrorDialogPayload({
          items: this.items,
          itemsWithError,
          previousItems,
        })
      )
    },

    handleDrag(event) {
      const moved = event?.moved ?? event
      if (moved?.oldIndex === undefined || moved?.newIndex === undefined) {
        this.handleChange()
        return
      }

      const previousItems = globalThis.structuredClone(this.items)

      const visibleOriginalIndexes = this.visibleItemEntries.map(
        entry => entry.originalIndex
      )
      const movedOriginalIndex = visibleOriginalIndexes[moved.oldIndex]

      if (!Number.isInteger(movedOriginalIndex)) {
        return
      }

      const regroupedItems = moveVisibleItemGroups(
        this.items,
        visibleOriginalIndexes,
        moved.oldIndex,
        moved.newIndex
      )

      if (!regroupedItems) {
        this.handleChange()
        return
      }

      this.items = regroupedItems

      if (this.shouldBlockDraggedItems()) {
        this.items = previousItems
        this.handleChange()
        this.$emit('requestWarning', {
          titleKey: 'routines.scoring.section_questions_required_warning_title',
          textKey:
            'routines.scoring.section_questions_required_warning_description',
          actionTextKey: 'common.button.close',
        })
        return
      }

      const itemsWithError = this.conditionalLogicEnabled
        ? getVisibilityRuleErrors(this.items)
        : []

      if (!_.isEmpty(itemsWithError)) {
        this.emitConditionalRemovalForErrors(itemsWithError, previousItems)
        return
      }

      this.handleChange()
    },

    shouldBlockDraggedItems() {
      if (!this.isSectionScoringActive) {
        return false
      }

      return hasOrphanQuestions(this.items)
    },

    getBlockedSectionDeleteAction(index) {
      const removedItem = this.items[index]
      const availableSections = this.buildAvailableSections(index)
      const keepsAtLeastOneSection = this.items.some(
        (item, itemIndex) =>
          item?.type === ITEM_TYPES.SEPARATOR && itemIndex !== index
      )

      if (!this.isSectionScoringActive) {
        return null
      }

      if (removedItem?.type !== ITEM_TYPES.SEPARATOR) {
        return null
      }

      if (_.isEmpty(getSectionQuestions(this.items, index))) {
        return null
      }

      if (!keepsAtLeastOneSection) {
        return {
          type: 'warning',
          titleKey: 'routines.scoring.section_required_warning_title',
          textKey: 'routines.scoring.section_required_warning_description',
          actionTextKey: 'common.button.close',
        }
      }

      return {
        type: 'deleteSectionDecision',
        sectionIndex: index,
        items: this.items,
        previousItems: this.items,
        availableSections,
        scoringType: this.checklist?.scoringType ?? null,
        source: 'checklist',
      }
    },

    buildAvailableSections(excludedIndex) {
      let currentSectionNumber = 0

      return this.items.reduce((sections, item, index) => {
        if (item?.type !== ITEM_TYPES.SEPARATOR) {
          return sections
        }

        if (index === excludedIndex) {
          return sections
        }

        // Flag-on only: conditioned sections never host relocated questions.
        if (
          this.conditionalLogicEnabled &&
          utils.checklists.hasVisibilityRule(item)
        ) {
          return sections
        }

        currentSectionNumber += 1

        sections.push({
          text:
            item?.name ||
            this.$t('routines.scoring.section_label', {
              number: currentSectionNumber,
            }),
          value: resolveScoringItemId(item, `scoring-item-${index}`),
        })

        return sections
      }, [])
    },
  },
}
</script>

<style lang="scss" scoped>
.ghost-item {
  opacity: 0.5;
}
.item-container {
  position: relative;

  &:hover {
    .item-drag-control {
      visibility: visible;
    }
  }
}

.item-drag-control {
  cursor: move;
  visibility: hidden;
}

.buttons-container {
  position: sticky;
  bottom: 0;
  background: linear-gradient(to bottom, transparent 10%, white 100%);
}
</style>
